import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import {
    addQueueMember,
    createQueue,
    deactivateQueue,
    fetchMyQueues,
    fetchQueue,
    fetchQueueNotificationStatus,
    fetchQueueTimeline,
    fetchQueueTree,
    fetchQueueWorkItemTallies,
    removeQueueMember,
    reactivateQueue,
    renameQueue,
    reparentQueue,
    searchUsersForQueue,
    setQueueOwner,
} from "./api";
import type {
    AddQueueMemberInput,
    CreateQueueInput,
    RemoveQueueMemberInput,
    RenameQueueInput,
    ReparentQueueInput,
    SetQueueOwnerInput,
    UserSearchCriteria,
} from "./api";
import type { QueueSummary, QueueWorkItemTally } from "./types";

const queueKeys = {
    all: ["queues"] as const,
    mine: ["queues", "mine"] as const,
    tree: ["queues", "tree"] as const,
    detail: (id: string) => ["queues", "detail", id] as const,
    timeline: (id: string) => ["queues", "timeline", id] as const,
    notificationStatus: (id: string) => ["queues", "notificationStatus", id] as const,
    // Tally key is segment-stable rather than carrying the id list as a
    // single string so partial invalidations (queryKey: ["queues",
    // "tallies"]) hit every active variant. The sorted id list is the
    // final segment so different argument orderings still share a cache
    // entry but distinct id sets are kept apart.
    tallies: (queueIds: readonly string[]) =>
        ["queues", "tallies", [...queueIds].sort().join(",")] as const,
    talliesAll: ["queues", "tallies"] as const,
    userSearch: (criteria: UserSearchCriteria) => ["queues", "userSearch", criteria] as const,
};

/**
 * Map shape exposed to row renderers: O(1) per-id lookup of Open/Pending
 * counts. Wrapped in a small helper instead of returning the raw array so
 * the (common) row renderer path stays sub-millisecond regardless of how
 * many queues are on screen.
 */
export type QueueWorkItemTallyMap = ReadonlyMap<string, QueueWorkItemTally>;

const EMPTY_TALLY_MAP: QueueWorkItemTallyMap = new Map();

export function useMyQueues() {
    const client = useApiClient();
    return useQuery({
        queryKey: queueKeys.mine,
        queryFn: () => fetchMyQueues(client),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });
}

export function useQueueTree() {
    const client = useApiClient();
    return useQuery({
        queryKey: queueKeys.tree,
        queryFn: () => fetchQueueTree(client),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });
}

export function useQueue(id: string) {
    const client = useApiClient();
    return useQuery({
        queryKey: queueKeys.detail(id),
        queryFn: () => fetchQueue(client, id),
        enabled: id.length > 0,
        staleTime: 30_000,
    });
}

/**
 * Queue audit history is demand-loaded by the maintenance-page dialog.
 * Keeping enablement explicit prevents every queue detail visit from
 * crossing the Activity Ledger boundary.
 */
export function useQueueTimeline(id: string, enabled: boolean) {
    const client = useApiClient();
    return useQuery({
        queryKey: queueKeys.timeline(id),
        queryFn: () => fetchQueueTimeline(client, id),
        enabled: enabled && id.length > 0,
        staleTime: 30_000,
    });
}

const EMPTY_MUTED_SET: ReadonlySet<string> = new Set();

export interface UseQueueNotificationStatusResult {
    /** User ids that have muted *this* queue; empty during the first load. */
    readonly mutedUserIds: ReadonlySet<string>;
    readonly isLoading: boolean;
    readonly error: Error | null;
}

/**
 * Per-member push-mute state for one queue, surfaced as an O(1)-lookup set of
 * muted user ids for the member-list renderer. Deliberately a separate query
 * from {@link useQueue}: keeping it off the queue-summary cache means queue
 * mutations (rename, add/remove member) can't overwrite the mute indicators,
 * and the list/tree views never pay for this read.
 */
export function useQueueNotificationStatus(id: string): UseQueueNotificationStatusResult {
    const client = useApiClient();
    const query = useQuery({
        queryKey: queueKeys.notificationStatus(id),
        queryFn: () => fetchQueueNotificationStatus(client, id),
        enabled: id.length > 0,
        staleTime: 30_000,
    });

    const mutedUserIds = useMemo<ReadonlySet<string>>(() => {
        const data = query.data;
        if (data === undefined) return EMPTY_MUTED_SET;
        const set = new Set<string>();
        for (const item of data.items) {
            if (item.muted) set.add(item.userId);
        }
        return set;
    }, [query.data]);

    return {
        mutedUserIds,
        isLoading: query.isLoading,
        error: query.error as Error | null,
    };
}

export interface UseQueueWorkItemTalliesResult {
    /** Map of queueId -> { open, pending }; empty during the first load. */
    readonly tallies: QueueWorkItemTallyMap;
    readonly isLoading: boolean;
    readonly isFetching: boolean;
    readonly error: Error | null;
}

/**
 * Fetch per-queue Open/Pending tallies for the supplied queue ids and
 * expose them as an O(1)-lookup map for row renderers. The hook is
 * deliberately list-shaped (one call covers every queue on screen)
 * rather than per-row so the indicator badges share a single fetch
 * regardless of how many queues are rendered.
 *
 * Cache key sorts the ids so different orderings hit the same entry;
 * SignalR will eventually patch the same cache key in place, so the
 * call sites won't need to change when it ships.
 */
export function useQueueWorkItemTallies(
    queueIds: readonly string[],
): UseQueueWorkItemTalliesResult {
    const client = useApiClient();

    // Sort once per id-set change so the queryKey and the queryFn argument
    // agree, and so unrelated render churn (e.g. queue order shuffles in
    // the sidebar) doesn't trigger a refetch. The joined key is the memo
    // input so a new array with the same ids does not recompute.
    const idsKey = queueIds.join(",");
    const sortedIds = useMemo(
        () => (idsKey.length === 0 ? [] : idsKey.split(",").sort()),
        [idsKey],
    );

    const query = useQuery({
        queryKey: queueKeys.tallies(sortedIds),
        queryFn: () => fetchQueueWorkItemTallies(client, sortedIds),
        enabled: sortedIds.length > 0,
        // Counts are time-sensitive (Pending flips to Open when due dates
        // pass) but a 15s window is plenty for an indicator badge — SignalR
        // will short-circuit this once it ships.
        staleTime: 15_000,
        refetchOnWindowFocus: true,
    });

    const tallies = useMemo<QueueWorkItemTallyMap>(() => {
        const data = query.data;
        if (data === undefined) return EMPTY_TALLY_MAP;
        const map = new Map<string, QueueWorkItemTally>();
        for (const t of data.tallies) {
            map.set(t.queueId, t);
        }
        return map;
    }, [query.data]);

    return {
        tallies,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error as Error | null,
    };
}

export function useCreateQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateQueueInput) => createQueue(client, input),
        onSuccess: (created: QueueSummary) => {
            queryClient.setQueryData(queueKeys.detail(created.id), created);
            void queryClient.invalidateQueries({ queryKey: queueKeys.mine });
            void queryClient.invalidateQueries({ queryKey: queueKeys.tree });
            void queryClient.invalidateQueries({ queryKey: queueKeys.talliesAll });
        },
    });
}

function makeQueueMutationHandlers(queryClient: ReturnType<typeof useQueryClient>) {
    return {
        onSuccess: (updated: QueueSummary) => {
            queryClient.setQueryData(queueKeys.detail(updated.id), updated);
            void queryClient.invalidateQueries({ queryKey: queueKeys.timeline(updated.id) });
            void queryClient.invalidateQueries({ queryKey: queueKeys.mine });
            void queryClient.invalidateQueries({ queryKey: queueKeys.tree });
            void queryClient.invalidateQueries({ queryKey: queueKeys.talliesAll });
        },
    };
}

export function useRenameQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RenameQueueInput) => renameQueue(client, input),
        ...makeQueueMutationHandlers(queryClient),
    });
}

export function useReparentQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: ReparentQueueInput) => reparentQueue(client, input),
        ...makeQueueMutationHandlers(queryClient),
    });
}

interface QueueDetailSnapshot {
    readonly previous: QueueSummary | undefined;
}

/**
 * Optimistic membership edits. The detail cache is patched before the
 * request leaves so the row appears (or leaves) on the keystroke; the server
 * copy replaces it on success and the snapshot restores it on error. The
 * provisional row reuses the member's userId as its key, so the DOM node
 * survives the swap and never re-animates.
 */
function makeMembershipMutationHandlers<TInput extends { readonly queueId: string }>(
    queryClient: ReturnType<typeof useQueryClient>,
    patch: (queue: QueueSummary, input: TInput) => QueueSummary,
) {
    const base = makeQueueMutationHandlers(queryClient);
    return {
        onMutate: async (input: TInput): Promise<QueueDetailSnapshot> => {
            const key = queueKeys.detail(input.queueId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData<QueueSummary>(key);
            if (previous !== undefined) {
                queryClient.setQueryData<QueueSummary>(key, patch(previous, input));
            }
            return { previous };
        },
        onError: (_error: Error, input: TInput, context: QueueDetailSnapshot | undefined) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(queueKeys.detail(input.queueId), context.previous);
            }
        },
        onSuccess: base.onSuccess,
    };
}

export function applyProvisionalMember(queue: QueueSummary, input: AddQueueMemberInput): QueueSummary {
    if (queue.members.some((m) => m.userId === input.userId)) return queue;
    return {
        ...queue,
        memberCount: queue.memberCount + 1,
        members: [
            ...queue.members,
            { userId: input.userId, displayName: input.displayName, addedAt: new Date().toISOString() },
        ],
    };
}

export function applyMemberRemoval(queue: QueueSummary, input: RemoveQueueMemberInput): QueueSummary {
    if (!queue.members.some((m) => m.userId === input.userId)) return queue;
    return {
        ...queue,
        memberCount: Math.max(0, queue.memberCount - 1),
        members: queue.members.filter((m) => m.userId !== input.userId),
    };
}

export function useAddQueueMember() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: AddQueueMemberInput) => addQueueMember(client, input),
        ...makeMembershipMutationHandlers(queryClient, applyProvisionalMember),
    });
}

export function useRemoveQueueMember() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: RemoveQueueMemberInput) => removeQueueMember(client, input),
        ...makeMembershipMutationHandlers(queryClient, applyMemberRemoval),
    });
}

export function useSetQueueOwner() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: SetQueueOwnerInput) => setQueueOwner(client, input),
        ...makeQueueMutationHandlers(queryClient),
    });
}

export function useDeactivateQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deactivateQueue(client, id),
        ...makeQueueMutationHandlers(queryClient),
    });
}

export function useReactivateQueue() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => reactivateQueue(client, id),
        ...makeQueueMutationHandlers(queryClient),
    });
}

// Caller-supplied criterion drives enablement: we only hit the backend when at
// least one usable filter is present and the free-text query (when used)
// satisfies the same >= 2-character minimum the backend enforces. This keeps
// the directory clear of single-character "browse all" floods and avoids
// burning a request on every keystroke.
function hasMeaningfulCriterion(criteria: UserSearchCriteria): boolean {
    const trimmedQ = (criteria.q ?? "").trim();
    if (trimmedQ.length >= 2) return true;
    return (
        (criteria.id ?? "").length > 0 ||
        (criteria.givenName ?? "").length > 0 ||
        (criteria.surname ?? "").length > 0 ||
        (criteria.email ?? "").length > 0
    );
}

export function useUserSearch(criteria: UserSearchCriteria) {
    const client = useApiClient();
    const memoCriteria = useMemo<UserSearchCriteria>(
        () => ({
            q: criteria.q?.trim() ?? "",
            id: criteria.id ?? "",
            givenName: criteria.givenName ?? "",
            surname: criteria.surname ?? "",
            email: criteria.email ?? "",
        }),
        [criteria.q, criteria.id, criteria.givenName, criteria.surname, criteria.email],
    );

    return useQuery({
        queryKey: queueKeys.userSearch(memoCriteria),
        queryFn: () => searchUsersForQueue(client, memoCriteria),
        enabled: hasMeaningfulCriterion(memoCriteria),
        staleTime: 15_000,
    });
}
